// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RouteNames } from 'router';
import { filter, get, isEmpty } from 'lodash';
import { NavigationUtil } from 'utils/NavigationUtil';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { EventSelector } from 'modules/event/selector';
import { EventActions } from 'modules/event/actions';
import { PageLoader } from 'components/molecules/pageLoader';
import EventDetailsBanner from 'components/organisms/eventDetailBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import ArtistDetailSection from 'components/templates/artistDetails';
import { CmsDetailsActions } from 'modules/cmsDetails/actions';
import { CmsDetailsSelector } from 'modules/cmsDetails/selector';
import UpcomingShowsSection from 'components/templates/upcomingShowsSection';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';
import TicketTypeSelection from 'components/organisms/ticketTypeSelection';
import RestaurantHeaderButtons from 'components/atoms/eventHeaderButtons';
import { CURRENT_COUNTRY_CODE } from 'config/env';
import useEventUpdater from 'hooks/useEventUpdater';
import RenderSEO from 'components/atoms/renderSEO';
type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const EventDetailsPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    contact,
    getContactDetails,
    getBannerData,
    getEventDetails,
    banner,
    footerLink,
    getFooterLinkData,
    artistsData,
    cmsDetailsData,
    getCmsDetailsData,
    upcomingShows,
    upcomingShowsOverlayData,
    getUpcomingEvents,
    getOutletEventTables,
    outletSeating,
    bookedStandingTickets,
    eventTableSeating,
  } = props;
  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');
  const [showTicketOverlay, setShowTicketOverlay] = useState(false);
  const eventSlug = props.match.params.eventSlug;
  const restaurantOutletId = isEmpty(banner)
    ? ''
    : get(banner.venues[0], 'outlet_id', '');
  const eventDates = isEmpty(banner)
    ? []
    : get(banner.venues[0], 'eventDates', '');

  useEffect(() => {
    getContactDetails();
    getVerticals();
    getFooterLinkData();
    getSiteSettings();
    getBannerData();
    getCmsDetailsData();
    getEventDetails(eventSlug);
    getUpcomingEvents();
  }, [eventSlug]);

  useEffect(() => {
    restaurantOutletId && getOutletEventTables(restaurantOutletId);
  }, [restaurantOutletId]);

  const countryCode = CURRENT_COUNTRY_CODE === '+1' ? 'USA' : 'INDIA';
  const showTicketSelection = useEventUpdater(eventDates, countryCode, true);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !banner ||
    !artistsData ||
    !cmsDetailsData ||
    !upcomingShowsOverlayData ||
    !upcomingShows ||
    !footerLink ||
    !outletSeating ||
    // !bookedStandingTickets ||
    !eventTableSeating
  ) {
    return null;
  }

  let ticketSection = {
    event_title: banner.title || '',
    description: banner.description || '',
    venues: (banner && banner.venues && banner.venues[0]) || [],
    seating_ticket_price: banner.seating_ticket_price,
    standing_capacity: banner.standing_capacity,
    standing_ticket_price: banner.standing_ticket_price,
  };

  const minSeatingPrice =
    Array.isArray(banner.seating_ticket_price) && banner.seating_ticket_price.length > 0
      ? banner.seating_ticket_price.reduce((min, curr) => Math.min(min, curr.price), Infinity)
      : 0;
  const hasTicketsAvailable = banner.standing_ticket_price > 0 || minSeatingPrice > 0;

  const selectedVertical = filter(verticals, ({ id }) => id === verticalId);
  const metaTagSEO = selectedVertical.length > 0 ? selectedVertical[0].metaTagSEO : '';

  const componentList = [
    ...(!isEmpty(banner)
      ? [
          {
            component: EventDetailsBanner,
            data: { ...banner, onBookNow: () => setShowTicketOverlay(true) },
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: RouteNames.musicEvents,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(artistsData) && !isEmpty(showTicketSelection)
      ? [
          {
            component: ArtistDetailSection,
            data: {
              artistsData,
              pastEventSection: false,
              cmsDetailsData,
              show_desktop_overlay_button: true,
            },
            theme: 'light-theme',
            sectionId: 'artist-details',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: RouteNames.musicEvents,
              },
              {
                breadcrumbName: 'Artist Details',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(banner) && !isEmpty(showTicketSelection)
      ? [
          {
            component: TicketTypeSelection,
            data: { ticketSection, eventTableSeating, bookedStandingTickets, showTicketOverlay, setShowTicketOverlay },
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'ticket-selection',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Select Tickets',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(upcomingShows)
      ? [
          {
            component: UpcomingShowsSection,
            data: {
              upcomingShows,
              upcomingShowsOverlayData,
              showNotificationButton: true,
              type: 'other-shows',
            },
            theme: 'dark-theme',
            sectionId: 'other-shows',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Other Shows You May Like',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Music & Events',
          breadcrumbLink: RouteNames.musicEvents,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Music & Events',
          breadcrumbLink: RouteNames.musicEvents,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
    <RenderSEO metaTags={metaTagSEO} currentRouteName={'music-and-events/:eventSlug'} />
    <PageTemplate
      theme={theme}
      componentList={componentList}
      updateTheme={e => setTheme(e)}
      verticals={verticals}
      siteSettings={siteSettings}
      toggleHeaderItem={true}
      type="artistHeader"
      headerArtistButton={artistsData}
      headerButtons={
        isMobile ? (
          !isEmpty(banner) && !isEmpty(showTicketSelection) && hasTicketsAvailable ? (
            <button
              className={'event-book-now-header-btn'}
              onClick={() => setShowTicketOverlay(true)}
            >
              Book Now
            </button>
          ) : null
        ) : (
          <RestaurantHeaderButtons artistsData={artistsData} />
        )
      }
    />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);
  const eventSlug = props.match.params.eventSlug;

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      FooterSelector.isLoading(state) ||
      CmsDetailsSelector.getCmsDetailsDataLoading(state) ||
      EventSelector.EventDetailsIsLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    footerLink: FooterSelector.getFooterData(state),
    banner: EventSelector.getEventDetailBanner(state),
    artistsData: EventSelector.getEventArtistsDetail(state),
    cmsDetailsData: CmsDetailsSelector.getCmsDetailsData(state),
    upcomingShowsOverlayData: EventSelector.getUpcomingEventsOverlayData(state),
    upcomingShows: EventSelector.getOtherEvents(state, eventSlug),
    outletSeating: EventSelector.getOutletEventSeating(state),
    eventTableSeating: EventSelector.getEventBookedTables(state),
    bookedStandingTickets: EventSelector.getEventStandingTickets(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getContactDetails,
    getVerticals,
    getSiteSettings,
    getBannerData,
  } = HomeActions;
  const {
    getEventDetails,
    getUpcomingEvents,
    getOutletEventTables,
    getBookedStandingTickets,
    getBookedTables,
  } = EventActions;
  const { getCmsDetailsData } = CmsDetailsActions;
  const { getFooterLinkData } = FooterActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getBannerData,
      getFooterLinkData,
      getCmsDetailsData,
      getEventDetails,
      getUpcomingEvents,
      getOutletEventTables,
      getBookedStandingTickets,
      getBookedTables,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EventDetailsPage);
